import * as Tone from "tone";

/**
 * Salamander Grand Piano sampler.
 * Samples are hosted by the tonejs-instruments project on jsDelivr.
 * The Sampler interpolates between the provided pitches, so we only need
 * a sparse set across the keyboard.
 */

// Official Tone.js audio samples — served from the tonejs.github.io GitHub
// Pages site. The previous nbrosowsky/tonejs-instruments mirror started
// returning 403 from jsDelivr and broke all in-browser piano playback.
const SALAMANDER_BASE = "https://tonejs.github.io/audio/salamander/";

const SAMPLE_MAP: Record<string, string> = {
  A0: "A0.mp3",
  C1: "C1.mp3",
  "D#1": "Ds1.mp3",
  "F#1": "Fs1.mp3",
  A1: "A1.mp3",
  C2: "C2.mp3",
  "D#2": "Ds2.mp3",
  "F#2": "Fs2.mp3",
  A2: "A2.mp3",
  C3: "C3.mp3",
  "D#3": "Ds3.mp3",
  "F#3": "Fs3.mp3",
  A3: "A3.mp3",
  C4: "C4.mp3",
  "D#4": "Ds4.mp3",
  "F#4": "Fs4.mp3",
  A4: "A4.mp3",
  C5: "C5.mp3",
  "D#5": "Ds5.mp3",
  "F#5": "Fs5.mp3",
  A5: "A5.mp3",
  C6: "C6.mp3",
  "D#6": "Ds6.mp3",
  "F#6": "Fs6.mp3",
  A6: "A6.mp3",
  C7: "C7.mp3",
  "D#7": "Ds7.mp3",
  "F#7": "Fs7.mp3",
  A7: "A7.mp3",
  C8: "C8.mp3",
};

let samplerInstance: Tone.Sampler | null = null;
let loadingPromise: Promise<Tone.Sampler> | null = null;

/**
 * Subscribers (typically MidiPlayer instances) that need to know when
 * something else has started a new sound and theirs has been silenced.
 */
const stopListeners = new Set<() => void>();

/** Subscribe to "another sound started, your sound is gone" events. */
export function onAudioStop(fn: () => void): () => void {
  stopListeners.add(fn);
  return () => {
    stopListeners.delete(fn);
  };
}

/**
 * Hard stop: silences the sampler, kills Tone.Transport, clears all scheduled
 * events, and notifies every subscriber so they can reset their UI state.
 * Called by every playback entry point before it starts something new.
 */
export function stopAllAudio(): void {
  try {
    Tone.getTransport().stop();
    Tone.getTransport().cancel();
    samplerInstance?.releaseAll();
  } catch {
    /* swallow — Tone may not be ready yet */
  }
  // Snapshot then iterate so a listener that unsubscribes on receipt
  // doesn't perturb the iteration order.
  Array.from(stopListeners).forEach((fn) => {
    try { fn(); } catch { /* ignore listener errors */ }
  });
}

/**
 * Synchronously kick the AudioContext while we're still inside the user
 * gesture. Browsers tie the gesture to the call site, so this MUST be
 * called from the click/keypress handler — NOT awaited from inside it.
 * Safe to call multiple times.
 */
export function kickAudio(): void {
  if (Tone.getContext().state !== "running") {
    void Tone.start();
  }
}

/** Resume the audio context. Awaitable — but kickAudio() should fire first. */
export async function ensureAudioReady(): Promise<void> {
  if (Tone.getContext().state !== "running") {
    await Tone.start();
  }
}

/**
 * Warm the browser's HTTP cache with the piano samples while the page is idle.
 *
 * The sampler is only built after the first click (it needs a running audio
 * context), so on a cold page the first chord a student clicks waited for ~30
 * sample downloads (≈2.4 MB) before it made a sound — seconds on a slow line.
 * Fetching the files ahead of time costs nothing to decode; when the click
 * comes, Tone's own fetches are served from cache. Skipped on Data Saver.
 */
let prefetched = false;
export function prefetchPianoSamples(): void {
  if (prefetched || typeof window === "undefined") return;
  prefetched = true;
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (conn?.saveData) return;
  const go = () => {
    for (const file of Object.values(SAMPLE_MAP)) {
      fetch(SALAMANDER_BASE + file, { mode: "cors", credentials: "omit" }).catch(() => { /* the real load retries */ });
    }
  };
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
  if (idle) idle(go, { timeout: 4000 });
  else window.setTimeout(go, 2500);
}

export function getPiano(): Promise<Tone.Sampler> {
  if (samplerInstance && samplerInstance.loaded) return Promise.resolve(samplerInstance);
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    // Build the sampler only after the context is running, so triggerAttackRelease
    // calls can be heard immediately and aren't queued into a suspended context.
    await ensureAudioReady();
    return await new Promise<Tone.Sampler>((resolve, reject) => {
      const sampler = new Tone.Sampler({
        urls: SAMPLE_MAP,
        baseUrl: SALAMANDER_BASE,
        release: 1.2,
        onload: () => {
          samplerInstance = sampler;
          resolve(sampler);
        },
        onerror: (err) => {
          loadingPromise = null; // allow retry
          reject(err);
        },
      }).toDestination();
      sampler.volume.value = -6;
    });
  })();
  return loadingPromise;
}

export type PitchOrNotes = string | string[];

/** Play a single pitch or chord (array) immediately. Cancels everything that
 *  was sounding before, so two playback buttons never overlap. */
export async function playNow(pitches: PitchOrNotes, duration: string | number = "2n", velocity = 0.85) {
  kickAudio();
  stopAllAudio();
  const piano = await getPiano();
  const ps = Array.isArray(pitches) ? pitches : [pitches];
  piano.triggerAttackRelease(ps, duration, undefined, velocity);
}

/** Play WITHOUT the global stop bus — notes ring together like a real piano.
 *  Use for live input (MIDI / on-screen keys during grading) and sequenced
 *  exercise playback, where a new note must NOT silence the previous one or
 *  kill a backing track. `playNow` remains the right call for one-shot demo
 *  buttons that should interrupt whatever else is sounding. */
export async function playSoft(pitches: PitchOrNotes, duration: string | number = "2n", velocity = 0.85) {
  kickAudio();
  const piano = await getPiano();
  const ps = Array.isArray(pitches) ? pitches : [pitches];
  piano.triggerAttackRelease(ps, duration, undefined, velocity);
}

/** Schedule a sequence of events on Tone.Transport. Returns a cleanup fn. */
export interface SequenceEvent {
  time: string | number; // bars:beats:sixteenths or seconds
  notes: string[];
  duration: string | number;
  velocity?: number;
}

export interface SequenceOptions {
  bpm?: number;
  /** Loop the whole sequence until the returned cleanup is called. */
  loop?: boolean;
  /** Loop boundary. Defaults to just past the last event. Seconds or BBS. */
  loopEnd?: string | number;
  /**
   * Fired on the audio frame each event sounds, via Tone.Draw — so a keyboard
   * highlight lands on the same frame as the note, not a setTimeout away.
   */
  onStep?: (index: number, ev: SequenceEvent) => void;
  /** Fired once the sequence runs out (never fires when looping). */
  onEnd?: () => void;
}

/**
 * Play a scored sequence on the audio clock.
 *
 * Everything visual should hang off `onStep` rather than a parallel chain of
 * setTimeouts: browser timers drift under load and get clamped to one second in
 * a backgrounded tab, which is exactly how a lesson exercise falls apart.
 */
export async function playSequence(
  events: SequenceEvent[],
  bpmOrOptions: number | SequenceOptions = 90
): Promise<() => void> {
  const opts: SequenceOptions =
    typeof bpmOrOptions === "number" ? { bpm: bpmOrOptions } : bpmOrOptions;
  const bpm = opts.bpm ?? 90;

  kickAudio();
  // Cancel anything that was previously playing — including any MidiPlayer.
  stopAllAudio();

  const piano = await getPiano();
  const t = Tone.getTransport();
  t.bpm.value = bpm;

  const ids: number[] = [];
  events.forEach((ev, i) => {
    const id = t.schedule((time) => {
      piano.triggerAttackRelease(ev.notes, ev.duration, time, ev.velocity ?? 0.85);
      if (opts.onStep) Tone.Draw.schedule(() => opts.onStep!(i, ev), time);
    }, ev.time);
    ids.push(id);
  });

  // Where the music actually ends, so we can loop or report completion.
  const lastSec = events.reduce((max, ev) => {
    const at = typeof ev.time === "number" ? ev.time : Tone.Time(ev.time).toSeconds();
    const dur = Tone.Time(ev.duration).toSeconds();
    return Math.max(max, at + dur);
  }, 0);

  if (opts.loop) {
    t.loop = true;
    t.loopStart = 0;
    t.loopEnd = opts.loopEnd ?? lastSec;
  } else {
    t.loop = false;
    if (opts.onEnd) {
      const endId = t.schedule(() => {
        // Defer: don't mutate the Transport from inside its own callback.
        setTimeout(() => opts.onEnd!(), 0);
      }, lastSec + 0.02);
      ids.push(endId);
    }
  }

  t.position = "0:0:0";
  t.start("+0.05");

  return () => {
    t.stop();
    t.cancel();
    t.loop = false;
    ids.forEach((id) => t.clear(id));
    samplerInstance?.releaseAll();
  };
}
